<template>
  <div>
    <van-nav-bar :title="t('resetPassword.title')" :border="false" class="custom-nav-bar">
      <template #left>
        <Button type="back" @click="onClickLeft" />
      </template>
    </van-nav-bar>
    <div class="m-[12px]">
      <div class="text-[13px] text-[#ffffff]">{{ t('resetPassword.confirmNewPassword') }}</div>

      <!-- 新密码输入框 -->
      <div class="auth-input-row mt-[30px]" :class="{ 'input-error': newPasswordError }">
        <input v-model="resetForm.newPassword" class="auth-input" :type="showNewPassword ? 'text' : 'password'"
          :placeholder="t('resetPassword.newPasswordPlaceholder')" @input="onNewPasswordInput"
          @blur="validateNewPassword">
        <img v-if="resetForm.newPassword" :src="getAssetsFile('home/closes.png')" alt="" class="h-[11px] w-[11px]"
          @click="resetForm.newPassword = ''">
        <img v-if="showNewPassword" :src="getAssetsFile('home/Preview@2x.png')" alt=""
          class="h-[11px] w-[11px] ml-[10px] cursor-pointer" style="object-fit: contain;" @click="toggleNewPassword">
        <img v-if="!showNewPassword" :src="getAssetsFile('home/Preview@2x (1).png')" alt=""
          class="h-[11px] w-[11px] ml-[10px] cursor-pointer" style="object-fit: contain;" @click="toggleNewPassword">
      </div>

      <!-- 新密码错误提示 -->
      <div v-if="newPasswordError" class="input-error-tip mt-[8px]">
        <img :src="getAssetsFile('home/@2x.png')" alt="" class="h-[11px] w-[11px] mr-[4px]">
        {{ newPasswordErrorMessage }}
      </div>

      <!-- 确认密码输入框 -->
      <div class="auth-input-row mt-[20px]" :class="{ 'input-error': confirmPasswordError }">
        <input v-model="resetForm.confirmPassword" class="auth-input" :type="showConfirmPassword ? 'text' : 'password'"
          :placeholder="t('resetPassword.confirmPasswordPlaceholder')" @input="onConfirmPasswordInput"
          @blur="validateConfirmPassword">
        <img v-if="resetForm.confirmPassword" :src="getAssetsFile('home/closes.png')" alt="" class="h-[11px] w-[11px]"
          @click="resetForm.confirmPassword = ''">
        <img v-if="showConfirmPassword" :src="getAssetsFile('home/Preview@2x.png')" alt=""
          class="h-[11px] w-[11px] ml-[10px] cursor-pointer" style="object-fit: contain;"
          @click="toggleConfirmPassword">
        <img v-if="!showConfirmPassword" :src="getAssetsFile('home/Preview@2x (1).png')" alt=""
          class="h-[11px] w-[11px] ml-[10px] cursor-pointer" style="object-fit: contain;"
          @click="toggleConfirmPassword">
      </div>

      <!-- 确认密码错误提示 -->
      <div v-if="confirmPasswordError" class="input-error-tip mt-[8px]">
        <img :src="getAssetsFile('home/@2x.png')" alt="" class="h-[11px] w-[11px] mr-[4px]">
        {{ confirmPasswordErrorMessage }}
      </div>

      <!-- 提交按钮 -->
      <div class="flex justify-center items-center mt-[30px]">
        <div class="anniu" @click="handleResetPassword" :class="{ 'auth-btn-disabled': isLoading }">
          {{ isLoading ? t('resetPassword.resetting') : t('resetPassword.confirm') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from "@/components/Button"
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { resetPassword } from '@/api/game'

const { t } = useI18n()
const router = useRouter()

const onClickLeft = () => {
  router.push('/security')
}

// 表单数据
const resetForm = ref({
  newPassword: '',
  confirmPassword: ''
})

// 显示/隐藏密码状态
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// 错误状态
const newPasswordError = ref(false)
const confirmPasswordError = ref(false)
const newPasswordErrorMessage = ref('')
const confirmPasswordErrorMessage = ref('')

// 加载状态
const isLoading = ref(false)

// 新密码输入处理
const onNewPasswordInput = () => {
  resetForm.value.newPassword = resetForm.value.newPassword.trim()
  validateNewPassword()
  // 如果确认密码已输入，重新验证确认密码
  if (resetForm.value.confirmPassword) {
    validateConfirmPassword()
  }
}

// 确认密码输入处理
const onConfirmPasswordInput = () => {
  resetForm.value.confirmPassword = resetForm.value.confirmPassword.trim()
  validateConfirmPassword()
}

// 验证新密码
const validateNewPassword = () => {
  const password = resetForm.value.newPassword
  if (!password) {
    newPasswordError.value = false
    newPasswordErrorMessage.value = ''
    return
  }

  if (password.length < 6 || password.length > 16) {
    newPasswordError.value = true
    newPasswordErrorMessage.value = t('resetPassword.passwordLengthError')
  } else {
    newPasswordError.value = false
    newPasswordErrorMessage.value = ''
  }
}

// 验证确认密码
const validateConfirmPassword = () => {
  const confirmPassword = resetForm.value.confirmPassword
  if (!confirmPassword) {
    confirmPasswordError.value = false
    confirmPasswordErrorMessage.value = ''
    return
  }

  if (confirmPassword !== resetForm.value.newPassword) {
    confirmPasswordError.value = true
    confirmPasswordErrorMessage.value = t('resetPassword.passwordMismatch')
  } else {
    confirmPasswordError.value = false
    confirmPasswordErrorMessage.value = ''
  }
}

// 切换密码显示状态
const toggleNewPassword = () => {
  showNewPassword.value = !showNewPassword.value
}

const toggleConfirmPassword = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

// 重置密码处理
const handleResetPassword = async () => {
  // 验证新密码
  if (!resetForm.value.newPassword) {
    newPasswordError.value = true
    newPasswordErrorMessage.value = t('resetPassword.newPasswordRequired')
    return
  }

  // 验证确认密码
  if (!resetForm.value.confirmPassword) {
    confirmPasswordError.value = true
    confirmPasswordErrorMessage.value = t('resetPassword.confirmPasswordRequired')
    return
  }

  // 检查是否有错误
  if (newPasswordError.value || confirmPasswordError.value) {
    return
  }

  try {
    isLoading.value = true

    // 调用重置密码API
    const response = await resetPassword(resetForm.value.newPassword)

    if (response.data) {
      window.$toast(t('resetPassword.resetSuccess'))
      router.push('/security')
    } else {
      window.$toast(t('resetPassword.resetFailed'))
    }

  } catch (error) {
    console.error('重置密码失败:', error)
    window.$toast(t('resetPassword.resetFailed'))
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.custom-nav-bar {
  height: 56px;
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px -2px 22px 0px rgba(53, 189, 128, 0.61);

  &>* {
    height: 100%;
  }
}

.auth-input-row {
  display: flex;
  border-radius: 6px;
  align-items: center;
  border: 1px solid #349C75;
  padding: 0 11px;
  margin-top: 20px;
  transition: border-color 0.3s ease;
}

.auth-input-row.input-error {
  border: 1px solid #ff4d4f !important;
}

.auth-input {
  flex: 1;
  height: 40px;
  border-radius: 8px;
  border: none;
  padding: 0 12px 0 0;
  background: none;
  color: #fff;
  font-size: 12px;
  caret-color: #50d38c;
}

.input-error-tip {
  display: flex;
  align-items: baseline;
  color: #ff4d4f;
  font-size: 12px;
}

.auth-btn-disabled {
  background: #126736 !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

input::placeholder {
  color: #096;
  font-size: 12px;
}

.anniu {
  width: 275px;
  height: 35px;
  background: linear-gradient(0deg, #379753 0%, #46C96D 100%);
  border-radius: 11px;
  color: #fff;
  font-size: 13px;
  text-align: center;
  line-height: 35px;
  font-weight: bold;
  cursor: pointer;
}
</style>

<route lang="json5">
{
  name: 'resetPassword',
  meta: {
    title: 'resetPassword'
  },
}
</route>
